import { SafeIntegerSchema } from '@sg/shared/src/valibot/schema/SafeInteger.type'
import { createNoise2D, type NoiseFunction2D } from 'simplex-noise'
import { DEV } from 'solid-js'
import { BufferAttribute, BufferGeometry, Color, Float32BufferAttribute } from "three"
import { array, object, parse, type InferOutput } from "valibot"
import { MountainEntitySchema, type MountainEntity } from '../../../core/entity/EntityType/Mountain'
import { createNewRNGConfig, next, nextIntRange, nextNumberRange } from '../../../core/rng/index'
import tmpRngWith from '../../../core/rng/tmpRngWith'
import isConwayAdjacent from '../../../core/tile_position_xy/isConwayAdjacent'
import { byD2Asc } from '../../../core/tile_position_xy/sortByD2'
import { abs, cos, floor, min, sin, sqrt, TWO_PI } from '../../../core/util/math/Math'
import { VERTICES_PER_TILE } from '../../consts'
import { getMountainElevationColor } from '../Mountain/colors'

const TiledInwardPointedMountainGeometryParametersSchema = object({
  // map offset x
  ox: SafeIntegerSchema,
  // map offset y
  oy: SafeIntegerSchema,
  // number of tiles in width
  width: SafeIntegerSchema,
  // number of tiles in height
  height: SafeIntegerSchema,
  // number of segments to divide each tile into
  segments: SafeIntegerSchema,
  // threejs world size of each tile
  tileSize: SafeIntegerSchema,
  // mountain game state entities
  ents: array(MountainEntitySchema),
  // midpoint of the map
  // mp: TilePositionXYSchema,
})

type TiledInwardPointedMountainGeometryParameters = InferOutput<typeof TiledInwardPointedMountainGeometryParametersSchema>

type Peak = {
  // threejs world position of the peak
  cx: number,
  cy: number,
  z: number,
  ent: MountainEntity,
  parent: Peak | null,
  d2: number, // distance squared
  near: Array<Peak>, // peaks that are nearby
  mains: Array<Peak>, // main peaks that are nearby
}

// https://chatgpt.com/share/67f997cb-aca0-8003-92e0-7e98143a92a2
/**
 * A geometry class for representing a plane
 * where the defining distinction from PlaneGeometry
 * is that the diagonal lines all point inwards.
 *
 * ```js
 * const geometry = new THREE.TiledInwardPointedMountainGeometry( 1, 1, 2, 2);
 * const material = new THREE.MeshBasicMaterial( { color: 0xffff00, side: THREE.DoubleSide } );
 * const plane = new THREE.Mesh( geometry, material );
 * scene.add( plane );
 * ```
 *
 * @augments BufferGeometry
 */
export default class TiledInwardPointedMountainGeometry extends BufferGeometry {

  type: string

  parameters: TiledInwardPointedMountainGeometryParameters

  /**
   * Constructs a special plane-like geometry.
   *
   * @param {TiledInwardPointedMountainGeometryParameters} [parameters=1] - the parameters
   */
  constructor(
    parameters: TiledInwardPointedMountainGeometryParameters
  ) {

    super()

    this.type = 'TiledInwardPointedMountainGeometry'

    /**
     * Holds the constructor parameters that have been
     * used to generate the geometry. Any modification
     * after instantiation does not change the geometry.
     *
     * @type {Object}
     */
    this.parameters = parameters
    const {
      ox,
      oy,
      width,
      height,
      segments,
      tileSize,
      ents,
      // mp,
    } = parameters

    if (DEV) {
      parse(TiledInwardPointedMountainGeometryParametersSchema, this.parameters)
      Object.freeze(this.parameters)
    }

    const width_half = width / 2
    const height_half = height / 2

    // I consider it a bug that this value is not 0
    // however, using it looks "close enough"
    const fixOffset = -1.5
    const worldOX = (width_half * tileSize) + fixOffset
    const worldOY = (height_half * tileSize) + fixOffset

    // subtract 1 from width and height to account for worldHalfSegments
    const gridX = segments * (width - 1)
    const gridY = segments * (height - 1)

    const gridX1 = gridX + 1
    const gridY1 = gridY + 1
    const gridX1_half = gridX1 / 2
    const gridY1_half = gridY1 / 2

    const segment_size = tileSize / segments
    const half_segments = floor(segments / 2)
    // we're going to cut off half of the edges of the plane
    // so that we're not processing mostly empty space
    const worldHalfSegments = half_segments * segment_size

    const indices: number[] = []
    const vertices: number[] = []
    const normals: number[] = []
    const uvs: number[] = []

    // ideally this value should be ~1 for small mountain ranges
    // and no more than 10 for huge mountain ranges
    const targetMidPointMax = ((ents.length - 2) + (width - 2) + (height - 2)) ** (0.4)
    // console.log('targetMidPointMax', targetMidPointMax, 'ents.length', ents.length)

    function getZFromMidPoint(x: number, y: number): number {
      const x1 = ((gridX1_half - abs(x)) / gridX1_half)
      const y1 = ((gridY1_half - abs(y)) / gridY1_half)
      const z = (x1 * y1)
      // return z

      const old_min = 0.25
      const old_max = 1
      const new_min = -2
      // const new_max = 2
      const new_max = targetMidPointMax

      const z_new = new_min + ((z - old_min) / (old_max - old_min)) * (new_max - new_min)
      return z_new
    }

    const basePeakZ = (4 * (VERTICES_PER_TILE / 10))
    const mainPeaks: Array<Peak> = ents.map((ent: MountainEntity) => {
      // Convert tile coordinates to world coordinates, adjusting for the center
      const cx = (0.5 + ent.x - ox - width / 2) * tileSize
      const cy = (0.5 + ent.y - oy - height / 2) * tileSize
      const z = basePeakZ + getZFromMidPoint(cx, cy)
      return { cx, cy, z, ent, parent: null, d2: 0, near: [], mains: [] }
    })

    const allPeaks: Array<Peak> = []
    // const nearPeaks: Array<Peak> = []

    const peakRadius = VERTICES_PER_TILE * 0.5

    mainPeaks.forEach((parent) => {
      const { ent } = parent
      allPeaks.push(parent)
      const rng = tmpRngWith(parent.ent.id)
      // smaller peaks budding off a main peak
      let remaining = nextIntRange(4, 8, rng)
      while (remaining-- > 0) {
        const distance = nextNumberRange(2, parent.z * 0.7, rng)
        const angle = next(rng) * TWO_PI
        const distRatio = 1 - (distance / peakRadius)
        if (distRatio > 0) {
          const z = ((parent.z * distRatio) + (1 * next(rng)))
          if (z > 1) {
            const cx = parent.cx + (cos(angle) * distance)
            const cy = parent.cy + (sin(angle) * distance)
            const peak = { cx, cy, z, ent, parent, d2: 0, near: [], mains: [] }
            allPeaks.push(peak)
          }
        }
      }
      // smaller peaks bridging two main peaks
      mainPeaks.forEach((peak1) => {
        const { ent: ent1 } = peak1
        if (isConwayAdjacent(ent, ent1)) {
          let remaining = nextIntRange(1, 4, rng)
          const baseZ = (parent.z + peak1.z)
          while (remaining-- > 0) {
            // average the x and y coordinates of the two peaks
            // to reduce the change of a smaller peak imposing itself
            // onto a non-mountainous area
            const cx = (nextNumberRange(parent.cx, peak1.cx, rng) + nextNumberRange(parent.cx, peak1.cx, rng)) / 2
            const cy = (nextNumberRange(parent.cy, peak1.cy, rng) + nextNumberRange(parent.cy, peak1.cy, rng)) / 2
            const z = nextNumberRange(baseZ * 0.01, baseZ * 0.35, rng)
            if (z > 1) {
              const peak = { cx, cy, z, ent, parent, d2: 0, near: [], mains: [] }
              allPeaks.push(peak)
            }
          }
        }
      })
    })

    // console.log('allPeaks', allPeaks.length)
    function getZFromNearbyPeaks(): number {
      let z0 = -10
      let z1: number
      // only look at the nearest 8 peaks
      // because they are already sorted by distance ascending
      const len = min(8, nearPeaks.length)
      for (let i = 0; i < len; i++) {
        const peak = nearPeaks[i]
        z1 = peak.z * (1 - (peak.d2 / peakRadius))
        if (z1 > z0) {
          z0 = z1
        }
      }
      return z0
    }

    // const xValues = []
    // const yValues = []
    // const zValues = []

    function getPeakD2(peak: Peak, x: number, y: number): number {
      return sqrt(((peak.cx - x) ** 2) + ((peak.cy - y) ** 2))
    }

    // pre-compute the neighboring peaks
    const nearDistanceThreshold = VERTICES_PER_TILE * 1.5
    const nearDistanceThreshold2 = nearDistanceThreshold ** 2
    const allPeaksLen = allPeaks.length
    for (let i = 0; i < allPeaksLen; i++) {
      const peak0 = allPeaks[i]
      peak0.near.push(peak0)
      if (!peak0.parent) {
        peak0.mains.push(peak0)
      }
      for (let j = i + 1; j < allPeaksLen; j++) {
        const peak1 = allPeaks[j]
        const xdist = abs(peak0.cx - peak1.cx)
        const ydist = abs(peak0.cy - peak1.cy)
        if (xdist < nearDistanceThreshold && ydist < nearDistanceThreshold) {
          const d2 = (xdist ** 2) + (ydist ** 2)
          if (d2 <= nearDistanceThreshold2) {
            peak0.near.push(peak1)
            peak1.near.push(peak0)
            if (!peak0.parent) {
              peak1.mains.push(peak0)
            }
            if (!peak1.parent) {
              peak0.mains.push(peak1)
            }
          }
        }
      }
    }

    function getClosestPeak(peaks: Array<Peak>, x: number, y: number): Peak {
      let closest: Peak = peaks[0]
      let closestD2 = Infinity
      for (let i = 0; i < peaks.length; i++) {
        const peak = peaks[i]
        // must calculate the distance squared
        // because we're getting the main before
        // before we can sort the near peaks
        const d2 = getPeakD2(peak, x, y)
        if (d2 < closestD2) {
          closest = peak
          closestD2 = d2
        }
      }
      return closest!
    }

    let nearPeaks: Array<Peak> = []

    function setNearPeaks(x: number, y: number): void {
      const mainPeak = getClosestPeak(mainPeaks, x, y)
      nearPeaks = mainPeak.near

      for (let i = 0; i < nearPeaks.length; i++) {
        const peak = nearPeaks[i]
        const d2 = getPeakD2(peak, x, y)
        peak.d2 = d2
      }
      // nearPeaks.sort(byDistanceAsc)
      nearPeaks.sort(byD2Asc)
    }

    // const peaks: Array<Peak> = []
    // const weights: Array<number> = []
    function weightedNoise2D(x: number, y: number): number {
      let result = 0
      let totalWeight = 0

      let mainPeaksCount = 0
      // const data = []
      for (let i = 0; i < nearPeaks.length; i++) {
        const peak = nearPeaks[i]
        if (peak.parent) {
          continue
        }
        mainPeaksCount++
        const value = getNoise2D(peak)(x, y)
        if (peak.d2 === 0) {
          return value
        }
        // const weight = 1 / peak.distance // invert distance
        // const weight = exp(-peak.distance / 2) // exponential decay
        // const weight = 1 / (peak.distance ** 2)  // Inverse square law
        const weight = 1 / (peak.d2)  // Inverse square law, using d2 instead of distance
        result += value * weight
        totalWeight += weight
        // data.push({ peak, value, weight })

        if (mainPeaksCount > 4 || peak.d2 > nearDistanceThreshold2) {
          break
        }
      }

      // probably distance === 0
      if (totalWeight > 0) {
        return result / totalWeight
      }
      // if (DEV) {
      //   console.log('nearPeaks', { x, y, mainPeaksCount, result, totalWeight }, nearPeaks)
      //   console.table(data)
      //   throw new Unexpected('weightedNoise2D: totalWeight === 0')
      // }
      return result
    }

    const noise2DCache = new WeakMap<Peak, NoiseFunction2D>
    function getNoise2D(peak: Peak): NoiseFunction2D {
      if (!noise2DCache.has(peak)) {
        const rng = createNewRNGConfig()
        rng.seed = rng.state = peak.ent.id
        noise2DCache.set(peak, createNoise2D(() => next(rng)))
      }
      return noise2DCache.get(peak)!
    }

    const colorBuffer = new Float32Array(3 * gridY1 * gridX1)
    const tmpColor = new Color()

    // Generate vertices, normals, uvs
    for (let iy = 0; iy < gridY1; iy++) {
      const y = -worldOY + worldHalfSegments + (iy * segment_size - height_half)

      for (let ix = 0; ix < gridX1; ix++) {
        const x = -worldOX + worldHalfSegments + (ix * segment_size - width_half)

        setNearPeaks(x, y)
        const mainPeakDist = sqrt(nearPeaks[0].d2)

        const z0 = getZFromNearbyPeaks()

        // penalty for being too far from a main peak
        const z1 = (mainPeakDist > peakRadius) ? (
          ((mainPeakDist - peakRadius) ** 1.5) * 0.2
        ) : 0
        const noiseValue0 = weightedNoise2D(x, y)
        const noiseValue1 = (noiseValue0 * ((mainPeakDist ** 1) / (peakRadius ** 2)))
        const noiseValue2 = (noiseValue1 ** 1.001)
        const nv2WasNan = isNaN(noiseValue2)
        const noiseValue3 = nv2WasNan ? (noiseValue1 + 0.3) : noiseValue2
        const z2 = (noiseValue3 * 0.5)
        const z = z0 - z1 - z2

        const colorZ = z - (weightedNoise2D(x / 2, y / 2) * 0.8)
        const color = getMountainElevationColor(colorZ, tmpColor)
        if (nv2WasNan) {
          color.r *= 0.95
          color.g *= 0.95
          color.b *= 0.95
        }

        const i3 = ((iy * gridX1) + ix) * 3
        colorBuffer[i3 + 0] = color.r
        colorBuffer[i3 + 1] = color.g
        colorBuffer[i3 + 2] = color.b

        // xValues.push(x)
        // yValues.push(y)
        // zValues.push(z)

        vertices.push(x, z, y)
        normals.push(0, 0, 1)
        uvs.push(ix / gridX, 1 - iy / gridY)
      }
    }

    // console.log('xValues.min.max', min(...xValues), max(...xValues))
    // console.log('yValues.min.max', min(...yValues), max(...yValues))
    // console.log('zValues.min.max', min(...zValues), max(...zValues))

    for (let iy = 0; iy < gridY; iy++) {
      const tile_y = floor(iy / segments)
      const centerY = (tile_y + 0.5) * segments

      for (let ix = 0; ix < gridX; ix++) {
        const tile_x = floor(ix / segments)
        const centerX = (tile_x + 0.5) * segments

        const a = ix + gridX1 * iy
        const b = ix + gridX1 * (iy + 1)
        const c = (ix + 1) + gridX1 * (iy + 1)
        const d = (ix + 1) + gridX1 * iy

        const dx = ix + 0.5 - centerX
        const dy = iy + 0.5 - centerY

        const isTopLeft = (dx < 0 && dy < 0)
        const isBottomRight = (dx >= 0 && dy >= 0)

        // Alternate triangulation per quadrant to "point inward"
        if (isTopLeft || isBottomRight) {
          // Diagonal: a -> c
          indices.push(a, b, c)
          indices.push(a, c, d)
        } else {
          // Diagonal: b -> d
          indices.push(a, b, d)
          indices.push(b, c, d)
        }
      }
    }

    this.setIndex(indices)
    this.setAttribute('position', new Float32BufferAttribute(vertices, 3))
    this.setAttribute('normal', new Float32BufferAttribute(normals, 3))
    this.setAttribute('uv', new Float32BufferAttribute(uvs, 2))
    this.setAttribute('color', new BufferAttribute(colorBuffer, 3))

    this.computeVertexNormals()
  }

  copy(source: TiledInwardPointedMountainGeometry) {

    super.copy(source)

    this.parameters = Object.assign({}, source.parameters)

    return this

  }

  /**
   * Factory method for creating an instance of this class from the given
   * JSON object.
   *
   * @param {Object} data - A JSON object representing the serialized geometry.
   * @return {TiledInwardPointedMountainGeometry} A new instance.
   */
  static fromJSON(data: TiledInwardPointedMountainGeometryParameters) {

    return new TiledInwardPointedMountainGeometry(data)

  }

}
